import { QuestionAnswerMap } from '@care-portals/api-interfaces';
import { calculateBMI, formatHeight } from '../../../utils/bmi';

const pcosQuestions = [
  {
    label: 'This visit evaluates symptoms related to Polycystic Ovary Syndrome (PCOS), hormone balance, and metabolic health. Your provider will review your symptoms, medical history, and goals to determine whether treatments such as progesterone therapy, metabolic support, or GLP-1 medications may be appropriate. The information you provide helps assess menstrual patterns, metabolic health, and any contraindications to treatment. Based on this review, your provider may recommend treatment, lifestyle guidance, request additional information, or advise follow-up with another healthcare provider if further evaluation is needed. Please answer all questions as accurately and completely as possible.',
    field: 'pcos_readyToStart',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [{ key: 'ready', value: "I'm ready to get started" }]
    }
  },
  {
    label:
      'Have you ever been diagnosed with Polycystic Ovary Syndrome (PCOS)?',
    field: 'pcos_diagnosedWithPcos',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'diagnosed', value: 'Yes, diagnosed by a healthcare provider' },
        { key: 'not_diagnosed', value: 'No, I do not have PCOS' }
      ]
    }
  },
  {
    label: 'What is your primary reason for seeking care today?',
    subLabel: 'Select all that apply',
    field: 'pcos_primaryReason',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'management', value: 'Management of PCOS' },
        {
          key: 'irregular_cycles',
          value: 'Irregular or absent menstrual cycles'
        },
        {
          key: 'androgen_symptoms',
          value:
            'Symptoms related to excess androgens (acne, hair growth, hair thinning)'
        },
        {
          key: 'weight_difficulty',
          value: 'Weight gain or difficulty losing weight'
        },
        {
          key: 'metabolic_concerns',
          value:
            'Metabolic concerns (insulin resistance, prediabetes, diabetes)'
        },
        { key: 'hormone_balance', value: 'General hormone balance' },
        { key: 'other', value: 'Other' }
      ]
    }
  },
  {
    label: 'Tell us more about the other symptoms that you are experiencing',
    field: 'pcos_primaryReasonOther',
    type: 'info',
    answer: {
      name: 'pcos_primaryReasonOther',
      type: 'text',
      rows: 4,
      placeholder: 'Enter details'
    }
  },
  {
    label: 'Have you experienced any of the following symptoms?',
    subLabel: 'Select all that apply',
    field: 'pcos_symptoms',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          key: 'hair_growth',
          value:
            'Excess facial or body hair growth (chin, upper lip, chest, abdomen)'
        },
        { key: 'acne_oily_skin', value: 'Acne or oily skin' },
        { key: 'scalp_thinning', value: 'Thinning hair on the scalp' },
        { key: 'difficulty_weight_loss', value: 'Difficulty losing weight' },
        {
          key: 'darkened_patches',
          value: 'Darkened skin patches (neck, armpits, groin)'
        },
        {
          key: 'none_of_the_above',
          value: 'None of the above',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    label: 'What is your:',
    field: 'pcos_weightHeightBMI',
    type: 'info',
    required: false,
    answer: {
      type: 'list',
      required: false,
      items: [
        {
          type: 'weightHeight',
          showLabel: true
        },
        {
          type: 'calculated',
          label: 'Your BMI is',
          answer: {
            formula: data => calculateBMI(data)
          }
        },
        {
          type: 'calculated',
          label: 'Height',
          answer: {
            formula: data => formatHeight(data)
          }
        },
        {
          type: 'calculated',
          label: 'Weight',
          answer: {
            formula: data => {
              const { weight, weightUnit } = data;
              return (weight || '') + ' ' + (weightUnit || '');
            }
          }
        }
      ]
    }
  },
  {
    label: 'What was your sex assigned at birth?',
    field: 'pcos_sexAssignedAtBirth',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'male', value: 'Male' },
        { key: 'female', value: 'Female' }
      ]
    }
  },
  {
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    field: 'pcos_pregnancy',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label: 'Consent (pregnancy)',
    field: 'pcos_pregnancyConsent',
    type: 'select',
    educationalSlide: `Treatment for Polycystic Ovary Syndrome (PCOS) in this program focuses on improving hormone balance and metabolic health. Some treatments, including progesterone therapy and medications such as GLP-1 receptor agonists (e.g., semaglutide), may restore ovulation in certain patients, which can increase the chance of pregnancy even in individuals who previously had difficulty ovulating. This program does not provide treatment for fertility or ovulation induction. Because some medications used in this program may pose risks during pregnancy, patients who are sexually active and able to become pregnant should take appropriate precautions to avoid pregnancy while receiving treatment. By proceeding, you acknowledge that treatment may improve fertility, that pregnancy should be avoided while using these medications, that you will use appropriate contraception if sexually active, and that you will notify your healthcare provider immediately if you become pregnant.

Please confirm your understanding:
    `,
    answer: {
      type: 'single_select',
      options: [
        {
          key: 'consent',
          value:
            'I understand that PCOS treatment may increase fertility and I will take appropriate precautions to avoid pregnancy while on treatment.'
        },
        {
          key: 'non_consent',
          value: 'I do not wish to proceed with treatment under these conditions.'
        }
      ]
    }
  },
  {
    label: 'Do you currently have a uterus?',
    field: 'pcos_hasUterus',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label: 'Do you experience irregular menstrual cycles?',
    field: 'pcos_cyclePattern',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'regular', value: 'My cycles are regular (every 24-35 days)' },
        { key: 'longer_than_35', value: 'My cycles are longer than 35 days' },
        { key: 'unpredictable', value: 'My cycles are unpredictable' },
        { key: 'skip_periods', value: 'I skip periods' },
        {
          key: 'rarely_or_never',
          value: 'I rarely or never have periods'
        }
      ]
    }
  },
  {
    label: 'On average, how many menstrual cycles do you have per year?',
    field: 'pcos_cyclesPerYear',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'ten_to_twelve', value: '10-12' },
        { key: 'six_to_nine', value: '6-9' },
        { key: 'three_to_five', value: '3-5' },
        { key: 'one_to_two', value: '1-2' },
        { key: 'none', value: 'None' }
      ]
    }
  },
  {
    label: 'When was your last menstrual period?',
    field: 'pcos_lastMenstrualPeriod',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'within_month', value: 'Within the last month' },
        { key: 'one_to_three_months', value: '1-3 months ago' },
        { key: 'three_to_six_months', value: '3-6 months ago' },
        { key: 'more_than_six_months', value: 'More than 6 months ago' },
        { key: 'dont_remember', value: 'I do not remember' }
      ]
    }
  },
  {
    label: 'Have you ever taken hormone therapy for cycle regulation?',
    subLabel: 'Select all that apply',
    field: 'pcos_hormoneTherapyHistory',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'progesterone', value: 'Progesterone' },
        { key: 'birth_control', value: 'Birth control pills' },
        { key: 'hormone_creams', value: 'Hormone creams' },
        { key: 'fertility_medications', value: 'Fertility medications' },
        {
          key: 'none',
          value: 'None',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    label:
      'Have you previously used progesterone therapy to regulate menstrual cycles?',
    field: 'pcos_progesteroneExperience',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'worked_well', value: 'Yes and it worked well' },
        { key: 'side_effect', value: 'Yes but I had a side effect' },
        {
          key: 'allergic_reaction',
          value: 'Yes but I had an allergic reaction'
        },
        { key: 'did_not_help', value: 'Yes but it did not help' },
        { key: 'no', value: 'No' },
        { key: 'unsure', value: 'Unsure' }
      ]
    }
  },
  {
    label: 'Tell us more about what side effects you experienced',
    field: 'pcos_progesteroneSideEffects',
    type: 'info',
    answer: {
      name: 'pcos_progesteroneSideEffects',
      type: 'text',
      rows: 4,
      placeholder: 'Enter details'
    }
  },
  {
    label: 'Have you ever been diagnosed with any of the following conditions?',
    subLabel: 'Select all that apply',
    field: 'pcos_progesteroneContraindications',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'breast_cancer', value: 'Breast cancer' },
        { key: 'uterine_cancer', value: 'Uterine or endometrial cancer' },
        {
          key: 'unexplained_vaginal_bleeding',
          value: 'Unexplained vaginal bleeding'
        },
        {
          key: 'blood_clot_history',
          value:
            'Blood clotting disorder or history of a blood clot (DVT) or pulmonary embolism (PE)'
        },
        {
          key: 'stroke_tia',
          value: 'History of stroke or mini-stroke (TIA)'
        },
        {
          key: 'cardiac_hypertension',
          value:
            'Coronary artery disease (CAD), congestive heart failure, or uncontrolled hypertension (blood pressure)'
        },
        {
          key: 'uncontrolled_mood_disorder',
          value:
            'Uncontrolled mood disorder such as depression, bipolar disorder, etc.'
        },
        {
          key: 'none',
          value: 'None of the above',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    label:
      'Have you ever used a GLP-1 medication (e.g. semaglutide, liraglutide, tirzepatide)?',
    field: 'pcos_usedGlp1',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label: 'Which medication did you take?',
    subLabel: 'Select all that apply',
    field: 'pcos_glp1Medication',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { key: 'semaglutide', value: 'Semaglutide (Wegovy/Ozempic)' },
        { key: 'tirzepatide', value: 'Tirzepatide (Mounjaro/Zepbound)' },
        { key: 'liraglutide', value: 'Liraglutide (Saxenda)' },
        { key: 'exenatide', value: 'Exenatide (Byetta)' },
        { key: 'dulaglutide', value: 'Dulaglutide (Trulicity)' }
      ]
    }
  },
  {
    label: 'Did you experience any side effects?',
    field: 'pcos_glp1SideEffects',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label: 'Tell us more about what you experienced',
    field: 'pcos_glp1SideEffectsDetails',
    type: 'info',
    answer: {
      name: 'pcos_glp1SideEffectsDetails',
      type: 'text',
      rows: 4,
      placeholder: 'Enter details'
    }
  },
  {
    label: 'Do any of these apply to you?',
    subLabel: 'Select all that apply',
    field: 'pcos_glp1Considerations',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          key: 'gastroparesis',
          value: 'Gastroparesis (Paralysis of your intestines)'
        },
        { key: 'high_triglycerides', value: 'Triglycerides over 600 at any point' },
        {
          key: 'pancreatic_history',
          value: 'Pancreatic cancer or pancreatitis'
        },
        {
          key: 'type1_or_insulin_dependent_diabetes',
          value: 'Type 1 Diabetes/Insulin-dependent diabetes'
        },
        { key: 'hypoglycemia', value: 'Hypoglycemia (low blood sugar)' },
        {
          key: 'thyroid_cancer_history',
          value: 'Personal or family history of medullary thyroid cancer'
        },
        {
          key: 'men2_history',
          value:
            'Personal or family history of Multiple Endocrine Neoplasia (MEN-2) syndrome'
        },
        { key: 'anorexia_or_bulimia', value: 'Anorexia or bulimia' },
        {
          key: 'siadh',
          value: 'Syndrome of Inappropriate Antidiuretic hormone'
        },
        {
          key: 'symptomatic_gallstones',
          value: 'Current symptomatic gallstones'
        },
        {
          key: 'gastric_bypass_recent',
          value: 'Gastric bypass in the past 6 months'
        },
        { key: 'liver_failure_cirrhosis', value: 'Liver failure/liver cirrhosis' },
        {
          key: 'ckd_stage_3b_or_greater',
          value: 'Chronic Kidney Disease Stage 3b or greater'
        },
        {
          key: 'current_gallstones_without_symptoms',
          value: 'Current gallstones without symptoms'
        },
        {
          key: 'past_gallbladder_removal',
          value: 'Past removal of your gallbladder'
        },
        {
          key: 'thyroid_issues',
          value: 'Hypothyroidism, Hyperthyroidism, or Thyroid Issues'
        },
        {
          key: 'none',
          value: 'None of the above',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    label: 'Gallstones consent',
    field: 'pcos_gallstonesConsent',
    type: 'select',
    educationalSlide: `Use of GLP-1 medications, including semaglutide, tirzepatide, and similar products and progesterone, has been associated with an increased risk of gallbladder or biliary issues, including the development or worsening of gallstones. If you have existing but asymptomatic gallstones, treatment may increase the chance that these stones become symptomatic, potentially leading to pain, inflammation, or the need for medical or surgical treatment. Notify your provider if you develop new upper abdominal pain, nausea, or vomiting during treatment. Knowing this risk, do you wish to continue? `,
    answer: {
      type: 'single_select',
      options: [
        {
          key: 'consent',
          value:
            'I have read and understand the information above and wish to proceed'
        },
        { key: 'non_consent', value: 'I do NOT wish to continue' }
      ]
    }
  },
  {
    label: 'Cholecystectomy consent',
    field: 'pcos_cholecystectomyConsent',
    type: 'select',
    educationalSlide: `You reported a history of gallbladder removal. This medication may still be appropriate but can alter fat and bile digestion, increasing the risk of side effects such as abdominal pain or diarrhea. It may also affect absorption of fat-soluble vitamins (A, D, E, and K); smaller meals, avoiding processed foods, and a daily multivitamin may help. Seek immediate care for severe abdominal pain, fever, or vomiting. `,
    answer: {
      type: 'single_select',
      options: [
        {
          key: 'consent',
          value:
            'I have read and understand the information above and wish to proceed'
        },
        { key: 'non_consent', value: 'I do NOT wish to continue' }
      ]
    }
  },
  {
    label:
      'Are you allergic to any GLP-1 products such as semaglutide, tirzepatide, Saxenda, or Trulicity?',
    field: 'pcos_glp1ProductAllergy',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label:
      'Do you take any of the following medications (insulin, sulfonylureas, meglitinides, or DPP-4 inhibitors)?',
    educationalSlide: `- Insulin
- Glimepiride (Amaryl) 
- Meglitinides such as repaglinide or nateglinide
- Glipizide (Glucotrol and Glucotrol XL)
- Glyburide (Micronase, Glynase, and Diabeta)
- Sitagliptin
- Saxagliptin
- Linagliptin
- Alogliptin`,
    field: 'pcos_interactingMeds',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { key: 'yes', value: 'Yes' },
        { key: 'no', value: 'No' }
      ]
    }
  },
  {
    label: 'What other information or questions do you have for the doctor?',
    field: 'pcos_otherInformation',
    type: 'info',
    answer: {
      name: 'pcos_otherInformation',
      type: 'text',
      rows: 4,
      placeholder: 'Optional details or questions'
    }
  },
  {
    label:
      'Consent (Progesterone Therapy and Compounded GLP-1 / GIP-GLP-1 Receptor Agonist Therapy)',
    field: 'pcos_medicationConsent',
    type: 'select',
    educationalSlide: `You are requesting evaluation and possible treatment with progesterone therapy and/or a compounded GLP-1 receptor agonist or GIP/GLP-1 receptor agonist medication, such as semaglutide or tirzepatide. These therapies may be used to support hormonal balance, metabolic health, and weight management depending on your clinical evaluation and medical history.

Progesterone Therapy
Progesterone is a naturally occurring hormone involved in regulating the menstrual cycle and supporting hormonal balance. Progesterone therapy may be used in certain individuals to help regulate menstrual cycles, support endometrial health, address symptoms related to hormone imbalance, or provide other hormone-related benefits as determined by your healthcare provider.

Potential benefits may include improved menstrual cycle regularity, hormonal balance, and symptom relief related to progesterone deficiency. However, progesterone therapy may cause side effects. Possible side effects may include fatigue, dizziness, breast tenderness, headaches, mood changes, bloating, or changes in menstrual bleeding patterns. Less commonly, hormone therapy may be associated with blood clots, allergic reactions, or other adverse events. Your provider will evaluate your medical history to determine if progesterone therapy is appropriate for you.

Progesterone therapy is not intended to treat infertility through this program. However, treatment that improves hormonal balance may restore ovulation in some individuals, which may increase the chance of pregnancy.

Compounded GLP-1 and GIP/GLP-1 Receptor Agonist Therapy
GLP-1 receptor agonists and GIP/GLP-1 receptor agonists are medications that mimic incretin hormones involved in regulating appetite, slowing gastric emptying, promoting fullness, and improving metabolic health.

Compounded medications are custom-prepared by licensed pharmacists. These formulations may allow for alternative dosing schedules, titration strategies, dosing frequencies, routes of administration, or the addition of ingredients intended to improve tolerability or reduce side effects. Compounded medications are not reviewed or approved by the U.S. Food and Drug Administration (FDA) and are not subject to the same testing, clinical trials, or manufacturing oversight as FDA-approved brand-name products. As a result, compounded medications may differ from FDA-approved products in consistency, potency, efficacy, and safety.

GLP-1 receptor agonists and GIP/GLP-1 receptor agonists are typically prescribed for individuals who are obese, or for individuals who are overweight with related medical conditions. Use of these medications in patients for PCOS is considered off-label, meaning the medication is prescribed in a manner not specifically approved by the FDA. Off-label prescribing is a common and accepted medical practice when clinically appropriate and based on medical judgment.

Potential benefits of treatment may include weight loss or weight management, improved blood glucose control, reduced cardiovascular risk, and overall improvement in metabolic health.

Common side effects may include:
- Nausea
- Vomiting
- Diarrhea
- Constipation
- Decreased appetite
- Indigestion

Serious or less common risks may include pancreatitis, low blood sugar, gallbladder disease, kidney injury or worsening kidney function, allergic reactions, and gastrointestinal motility disorders such as gastroparesis. In rare cases, serious complications including hospitalization or death may occur.

Pregnancy and Fertility Considerations
Some treatments in this program, including hormone therapy and metabolic therapies, may improve hormonal balance and restore ovulation in certain patients, which may increase the chance of pregnancy even in individuals who previously had irregular ovulation.

This program does not provide treatment for fertility purposes.

GLP-1 and GIP/GLP-1 receptor agonist medications should not be used during pregnancy or while breastfeeding, as they may pose risks to a developing baby.

These medications may also reduce the effectiveness of oral contraceptives. If you use oral birth control, you should continue it with a barrier method such as condoms for the first month after starting treatment and for one month after any dose increase. You may choose to switch to a non-oral contraceptive method such as an intrauterine device or implant prior to starting treatment.

After stopping GLP-1 medications, effective contraception should be continued for at least two months before attempting to conceive to ensure the medication has cleared from your system.

Monitoring and Follow-Up
Ongoing follow-up may be required to monitor your response to treatment and assess for potential side effects. Medication doses may be adjusted based on tolerability and clinical response.

Patient Attestation
Please attest to the following confirming that all information you have provided is true and complete.

I verify that I am the patient completing this intake form. I confirm that I have reviewed and understood the questions asked of me and that the information I have provided is accurate and complete to the best of my knowledge. I understand that it is critical to share complete and accurate health information with my healthcare provider. I will not hold the provider or affiliated medical practice responsible for any oversights or omissions, whether intentional or unintentional, in the information I provided.

By proceeding with treatment, you acknowledge that compounded medications are not FDA-approved, that they may differ from FDA-approved products in safety and effectiveness, and that you understand the potential benefits, risks, side effects, pregnancy-related precautions, and possible off-label use associated with progesterone therapy and GLP-1 or GIP/GLP-1 receptor agonist therapy. You agree to follow prescribed instructions, participate in recommended follow-up, and promptly report any concerning symptoms or side effects.
`,
    answer: {
      type: 'single_select',
      options: [
        {
          key: 'consent',
          value:
            'I have read the above information and I do consent and wish to move forward'
        },
        {
          key: 'non_consent',
          value:
            'I have read the above information and I do not wish to continue'
        }
      ]
    }
  },
  {
    field: 'pcos_complete',
    isLast: true,
    label: 'Thank you for completing this questionnaire, please select "Finish" to submit',
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    field: 'pcos_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    type: 'info',
    isLast: true,
    blockQuestionFlow: true,
    answer: {},
    extras: { sendToBeluga: false }
  }
] as const;

const _type = pcosQuestions;
export type PCOSInitialQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof _type>
>;

export default pcosQuestions;
